import React from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, Upload, Users } from 'lucide-react';
import type { DocumentCategory, DocumentResponse } from '@timesheet/schemas';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { EmptyState } from '../shared/empty-state';
import { DocumentRow } from './DocumentRow';
import { ctaLabel } from './documents.utils';

interface DocumentListProps {
  category: DocumentCategory;
  documents: DocumentResponse[];
  loading: boolean;
  /** Whether the viewer sees other people's documents in this tab. */
  showPerson: boolean;
  canManage: boolean;
  hasFilters: boolean;
  /** MANAGER without direct reports: explain why the contracts tab is empty. */
  managerWithoutReports?: boolean;
  onOpen: (doc: DocumentResponse) => void;
  onDownload: (doc: DocumentResponse) => void;
  onUpload?: () => void;
}

const RowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 px-3 py-2.5">
    <Skeleton className="h-9 w-9 rounded-md" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-4 w-44" />
      <Skeleton className="h-3 w-60" />
    </div>
    <Skeleton className="h-4 w-4" />
  </div>
);

export const DocumentList: React.FC<DocumentListProps> = ({
  category,
  documents,
  loading,
  showPerson,
  canManage,
  hasFilters,
  managerWithoutReports = false,
  onOpen,
  onDownload,
  onUpload,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card className="divide-y overflow-hidden">
        {[1, 2, 3, 4].map((i) => (
          <RowSkeleton key={i} />
        ))}
      </Card>
    );
  }

  if (documents.length === 0) {
    const emptyTitles: Record<DocumentCategory, string> = {
      GENERALE: t('documents.empty.GENERALE', 'Nessun documento generale'),
      BUSTE_PAGA: t('documents.empty.BUSTE_PAGA', 'Nessuna busta paga'),
      CONTRATTI: t('documents.empty.CONTRATTI', 'Nessun contratto'),
      AZIENDALI: t('documents.empty.AZIENDALI', 'Nessun documento aziendale'),
    };
    const description = managerWithoutReports
      ? t('documents.empty.noReports', 'Nessun collaboratore collegato a te: chiedi a un amministratore di assegnarti i riporti.')
      : hasFilters
        ? t('documents.empty.filtered', 'Nessun documento corrisponde ai filtri.')
        : undefined;
    return (
      <Card>
        <EmptyState
          icon={managerWithoutReports ? Users : FolderOpen}
          title={emptyTitles[category]}
          description={description}
          action={canManage && onUpload ? { label: ctaLabel(category, t), onClick: onUpload, icon: Upload } : undefined}
        />
      </Card>
    );
  }

  return (
    <Card className="divide-y overflow-hidden">
      {documents.map((doc) => (
        <DocumentRow key={doc.id} doc={doc} showPerson={showPerson} onOpen={onOpen} onDownload={onDownload} />
      ))}
    </Card>
  );
};
